import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, ArrowRight, Sparkles, User, FileText, Camera, Video,
  CheckCircle2, Clock, AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { VersionStatus } from "@/types/teacherProfile";

interface ProfileCompletionBannerProps {
  completedFields?: Record<string, boolean>;
  setupPath: string;
  draftStatus?: VersionStatus | null;
  hasActiveVersion?: boolean;
  isLoading?: boolean;
}

const MANDATORY_FIELDS = [
  { key: "about",       label: "About",       icon: User     },
  { key: "description", label: "Bio",         icon: FileText },
  { key: "photo",       label: "Photo",       icon: Camera   },
  { key: "video",       label: "Intro Video", icon: Video    },
] as const;

export function ProfileCompletionBanner({
  completedFields = {},
  setupPath,
  draftStatus,
  hasActiveVersion = false,
  isLoading = false,
}: ProfileCompletionBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  if (isLoading) return null;

  const fields = MANDATORY_FIELDS.map((f) => ({
    ...f,
    completed: !!completedFields[f.key],
  }));

  const completedCount = fields.filter((f) => f.completed).length;
  const totalCount = fields.length;
  const allMandatoryDone = completedCount === totalCount;

  if (hasActiveVersion && !draftStatus) return null;
  if (dismissed) return null;

  const DismissButton = () => (
    <button
      onClick={() => setDismissed(true)}
      className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-black/5 transition-colors shrink-0"
      aria-label="Dismiss"
    >
      <X className="w-3 h-3" />
    </button>
  );

  // ── Under review ─────────────────────────────────────────────────
  if (draftStatus === 'PENDING_REVIEW') {
    return (
      <div className="flex items-center gap-3 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 mb-6">
        <Clock className="w-4 h-4 text-sky-500 shrink-0" />
        <p className="text-sm text-foreground flex-1">
          <span className="font-semibold">Profile under review</span>
          <span className="text-muted-foreground"> · Usually approved within 1–2 business days.</span>
        </p>
        <DismissButton />
      </div>
    );
  }

  // ── Rejected ─────────────────────────────────────────────────────
  if (draftStatus === 'REJECTED') {
    return (
      <div className="flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 mb-6">
        <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
        <p className="text-sm text-foreground flex-1">
          <span className="font-semibold">Profile needs revision</span>
          <span className="text-muted-foreground"> · An admin requested changes before your profile can go live.</span>
        </p>
        <button
          onClick={() => navigate(setupPath)}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold transition-colors shadow-sm shadow-rose-500/30 shrink-0"
        >
          View Feedback <ArrowRight className="w-3 h-3" />
        </button>
        <DismissButton />
      </div>
    );
  }

  // ── All done, not yet submitted ───────────────────────────────────
  if (allMandatoryDone) {
    return (
      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-6">
        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
        <p className="text-sm text-foreground flex-1">
          <span className="font-semibold">Profile complete</span>
          <span className="text-muted-foreground"> · Submit for admin review to start accepting students.</span>
        </p>
        <button
          onClick={() => navigate(setupPath)}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors shadow-sm shadow-emerald-500/30 shrink-0"
        >
          Submit for Review <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // ── Incomplete ────────────────────────────────────────────────────
  const pct = Math.round((completedCount / totalCount) * 100);
  const remaining = totalCount - completedCount;

  return (
    <div className="rounded-xl mb-6 overflow-hidden bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100">

      {/* Header row */}
      <div className="flex items-center gap-3 px-4 pt-3.5 pb-2">
        <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-slate-800 leading-tight">
            {completedCount === 0
              ? "Complete your profile to appear in student search"
              : remaining === 1
                ? "One section left — almost there!"
                : `${remaining} sections left before you can go live`}
          </p>
          <p className="text-sm text-slate-500 leading-tight mt-0.5">
            Complete all sections to submit for review and start accepting students.
          </p>
        </div>
        <DismissButton />
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-2.5">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-indigo-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                background: "linear-gradient(90deg, #6366f1, #7c3aed)",
              }}
            />
          </div>
          <span className="text-xs font-semibold text-indigo-600 shrink-0">{completedCount}/{totalCount}</span>
        </div>
      </div>

      {/* Section chips + CTA */}
      <div className="flex items-center gap-1.5 flex-wrap px-4 pb-3.5 border-t border-indigo-100 pt-2.5">
        {fields.map((field) => {
          const Icon = field.icon;
          return (
            <div
              key={field.key}
              className={cn(
                "flex items-center gap-1.5 text-sm px-3 py-1 rounded-full font-medium transition-colors",
                field.completed
                  ? "bg-indigo-100 text-indigo-700"
                  : "bg-white/70 text-slate-500"
              )}
            >
              <Icon className="w-3 h-3" />
              {field.label}
              {field.completed && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
            </div>
          );
        })}
        <div className="ml-auto">
          <button
            onClick={() => navigate(setupPath)}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors shadow-sm shadow-indigo-500/30"
          >
            {completedCount === 0 ? "Start Setup" : "Continue"} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
